import Loadable from 'react-loadable';
import { ConnectedRouter } from 'connected-react-router';
import { useConfig } from 'Contexts/Config';
import React from 'react';
import { Provider } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import store, { history } from 'Src/store';
import { ADMIN_ACCOUNTS_ROUTES, ADMIN_ACCOUNTS_RETURN_TO } from 'Src/admin_money_accounts/flow/AdminAccounts.routes';

import { isProd as isProdEnv } from 'Utils/env';
import { LogoutToggle } from 'Utils/devtools/LogoutToggle';
import { PrevPageProvider } from 'Providers/PrevPage';
import AuthenticationRequired from 'Components/AuthenticationRequired';
import { Application } from 'Components/RouteWrappers/Application';
import { EmailVerified } from 'Components/RouteWrappers/EmailVerified';
import { Authenticated } from 'Components/RouteWrappers/Authenticated';
import { FunnelWrappers } from 'Components/RouteWrappers/FunnelRoute';
import Urls from 'Containers/Urls';
import PrivateRoute from './PrivateRoute';
import {
  AboutKueski,
  Account,
  AccountAdd,
  AccountContact,
  AddAccount,
  AddCard,
  AddCardAccount,
  AutomaticRefundsPage,
  CompletedKYC,
  ContactInformation,
  ContactInformationPhone,
  CreateApplicationError,
  CreditCapacityOrderSuccess,
  CreditDetail,
  DirectDebit,
  DirectDebitSantander,
  DomicSubscription,
  EmailVerification,
  EmailVerificationToken,
  EvaluationCardRegistration,
  ExternalError,
  Home,
  ListAccounts,
  ListCards,
  ListCardsAndAccounts,
  LoanDetail,
  LoanPaymentMethods,
  PagerApply,
  PagerApproving,
  PagerCanceled,
  PagerChannels,
  PagerCodeChannel,
  PagerEditMobile,
  PagerIdentityVerification,
  PagerOffering,
  PagerProfile,
  PagerRedesignLanding,
  PagerRejected,
  PagerScoring,
  PagerTransfer,
  PagerValidating,
  PaymentConfirmationDetails,
  PaymentStatus,
  PersonalLoan,
  SavingsPage,
  StatementsPage,
  TransactionDetail,
  UserVerification,
  UserVerificationResult,
  ValidateIdentity,
  Verification2FA,
} from './LoadableComponents';
import { AuroraInitializers } from './pages/AuroraInitializers';

const FeatureFlagsToggle = Loadable({
  loader: () => import(/* webpackChunkName: "DevTools" */ 'Utils/devtools/FeatureFlagsToggle'),
  loading: () => null,
  modules: ['Pager'],
});

const Routes = () => {
  const {
    CASH_INSTALLMENTS: cashInstallmentsEnabled,
    DIRECT_DEBIT_SANTANDER_PAYMENT_METHOD: directDebitSantanderPaymentMethod,
    DOMIC_SUBSCRIPTION_V1: isDomicSubscriptionEnabled,
    VERIFY_ACCOUNT: verifyAccount,
    CONTACT_INFO_MENU,
    FUNNEL_PHONE_EDITION: funnelEditPhoneEnabled,
    ADMIN_ACCOUNTS_FLOW: adminAccountsFlow,
    ADMIN_ACCOUNTS_FLOW_TO_FUNNEL_4_ECOSYSTEM: adminAccountsFlowToFunnel4Ecosytem,
    WIP_FTII_UNIFY_SANTANDER_CONEKTA: wipFTIIUnifySantanderConekta,
    MY_ACCOUNT_V2: myAccountV2,
  } = useConfig().features;

  return (
    <Provider store={store}>
      <ConnectedRouter basename={Urls.root} history={history}>
        <PrevPageProvider>
          <AuroraInitializers />
          {!isProdEnv() ? (
            <>
              <AuthenticationRequired>
                <LogoutToggle />
              </AuthenticationRequired>
              <FeatureFlagsToggle />
            </>
          ) : null}
          <Switch>
            <Route
              exact
              path={Urls.root}
              component={PagerRedesignLanding}
            />
            <Route
              exact
              path={Urls.landing}
              component={PagerRedesignLanding}
            />
            {verifyAccount && (
              <Route
                exact
                path={Urls.verifyAccount}
                component={EmailVerification}
              />
            )}
            <Route path={Urls.accountAdd}>
              <Authenticated>
                <EmailVerified>
                  <AccountAdd />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route exact path={Urls.home}>
              <Authenticated>
                <EmailVerified>
                  <Home />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route path={Urls.apply}>
              <FunnelWrappers status="filling">
                <PagerApply />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.scoring}>
              <FunnelWrappers status="scoring">
                <PagerScoring />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.approving}>
              <FunnelWrappers status="approving">
                <PagerApproving />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.identityVerification}>
              <FunnelWrappers>
                <PagerIdentityVerification />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.validating}>
              <FunnelWrappers status="validating">
                <PagerValidating />
              </FunnelWrappers>
            </Route>
            {
              funnelEditPhoneEnabled && (
                <Route path={Urls.edit}>
                  <FunnelWrappers status="validating">
                    <PagerEditMobile />
                  </FunnelWrappers>
                </Route>
              )
            }
            <Route path={Urls.offering}>
              <FunnelWrappers status="offering">
                <PagerOffering />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.transfer}>
              <FunnelWrappers status="transfer">
                <PagerTransfer />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.cancelled}>
              <FunnelWrappers status="cancelled">
                <PagerCanceled />
              </FunnelWrappers>
            </Route>
            <Route path={Urls.rejected}>
              <FunnelWrappers status="rejected">
                <PagerRejected />
              </FunnelWrappers>
            </Route>
            <Route exact path={Urls.loan}>
              <FunnelWrappers status="loan">
                <CreditCapacityOrderSuccess />
              </FunnelWrappers>
            </Route>
            <Route exact path={Urls.authorized}>
              <FunnelWrappers status="authorized">
                <CreditCapacityOrderSuccess />
              </FunnelWrappers>
            </Route>
            <Route exact path={Urls.completed}>
              <FunnelWrappers status="completed">
                <CompletedKYC />
              </FunnelWrappers>
            </Route>
            <Route exact path={Urls.createApplicationError}>
              <FunnelWrappers>
                <CreateApplicationError />
              </FunnelWrappers>
            </Route>
            <PrivateRoute
              exact
              path={Urls.profile}
              status="loan"
              component={PagerProfile}
            />
            {
              isDomicSubscriptionEnabled && (
                <Route exact path={Urls.domicSubscription}>
                  <Authenticated>
                    <EmailVerified>
                      <DomicSubscription />
                    </EmailVerified>
                  </Authenticated>
                </Route>
              )
            }
            <Route exact path={Urls.transactionDetail}>
              <Authenticated>
                <EmailVerified>
                  <TransactionDetail />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route exact path={Urls.savings}>
              <Authenticated>
                <EmailVerified>
                  <SavingsPage />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route exact path={Urls.refunds}>
              <Authenticated>
                <EmailVerified>
                  <Application>
                    <AutomaticRefundsPage />
                  </Application>
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route
              exact
              path={Urls.instoreCustomer}
            >
              <Redirect to={Urls.channels} />
            </Route>
            <Route exact path={Urls.channels}>
              <FunnelWrappers>
                <PagerChannels />
              </FunnelWrappers>
            </Route>
            <Route exact path={Urls.codeChannel}>
              <FunnelWrappers status="approving">
                <PagerCodeChannel />
              </FunnelWrappers>
            </Route>
            <PrivateRoute
              path={Urls.directDebit}
              status="loan"
              component={DirectDebit}
            />
            {directDebitSantanderPaymentMethod && !wipFTIIUnifySantanderConekta && (
              <PrivateRoute
                path={Urls.directDebitSantander}
                status="loan"
                component={DirectDebitSantander}
              />
            )}
            {directDebitSantanderPaymentMethod && (
              <PrivateRoute
                path={Urls.directDebitSantanderPaymentResponse}
                status="loan"
                component={PaymentStatus}
              />
            )}
            <PrivateRoute
              path={Urls.paymentMethods}
              status="loan"
              component={LoanPaymentMethods}
            />

            <PrivateRoute
              path={Urls.paymentConfirmationDetails}
              status="loan"
              component={PaymentConfirmationDetails}
            />

            <PrivateRoute
              path={Urls.statements}
              status="loan"
              component={StatementsPage}
            />
            {cashInstallmentsEnabled && (
              <PrivateRoute
                path={Urls.cashInstallments}
                status="loan"
                component={PersonalLoan}
              />
            )}
            <PrivateRoute
              path={Urls.loan_detail}
              status="loan"
              component={LoanDetail}
            />
            <PrivateRoute
              path={Urls.credit}
              status="loan"
              component={CreditDetail}
            />
            <Route
              exact
              path={Urls.voucherDetail}
            >
              <Redirect to={Urls.channels} />
            </Route>
            {CONTACT_INFO_MENU && (
              <PrivateRoute
                exact
                path={Urls.contactInformation}
                status="loan"
                component={ContactInformation}
              />
            )}
            <PrivateRoute
              exact
              path={Urls.contactInformationPhone}
              status="loan"
              component={ContactInformationPhone}
            />
            <PrivateRoute
              exact
              path={Urls.validateIdentity}
              status="loan"
              component={ValidateIdentity}
            />
            {
              adminAccountsFlow && (
                <PrivateRoute
                  exact
                  path={ADMIN_ACCOUNTS_ROUTES.MANAGE_CARDS}
                  component={ListCards}
                />
              )
            }
            {
              adminAccountsFlow && (
                <PrivateRoute
                  exact
                  path={ADMIN_ACCOUNTS_ROUTES.CHECKOUT_FORM}
                  component={AddCard}
                />
              )
            }
            {
              adminAccountsFlow && (
                <PrivateRoute
                  exact
                  path={ADMIN_ACCOUNTS_ROUTES.MANAGE_ACCOUNTS}
                  component={ListAccounts}
                />
              )
            }
            {
              adminAccountsFlow && (
                <PrivateRoute
                  exact
                  path={ADMIN_ACCOUNTS_ROUTES.ACCOUNT_FORM}
                  component={AddAccount}
                />
              )
            }
            <PrivateRoute
              exact
              path={ADMIN_ACCOUNTS_ROUTES.MANAGE_REIMBURSEMENT_ACCOUNTS}
              component={ListCardsAndAccounts}
            />
            <PrivateRoute
              exact
              path={ADMIN_ACCOUNTS_ROUTES.ACCOUNT_REIMBURSEMENT_FORM}
              component={AddCardAccount}
            />
            <PrivateRoute
              exact
              path={ADMIN_ACCOUNTS_ROUTES.ACCOUNT_DOMIC_SUBSCRIPTION_FORM}
              component={AddCardAccount}
            />
            <PrivateRoute
              exact
              path={ADMIN_ACCOUNTS_ROUTES.MANAGE_DOMIC_SUBSCRIPTION_ACCOUNTS}
              component={ListCardsAndAccounts}
            />
            {
              adminAccountsFlow && adminAccountsFlowToFunnel4Ecosytem && (
                <Route exact path={ADMIN_ACCOUNTS_RETURN_TO.FUNNEL_4_ECOSYSTEM}>
                  <FunnelWrappers>
                    <EvaluationCardRegistration />
                  </FunnelWrappers>
                </Route>
              )
            }
            {myAccountV2 && (
              <Route
                exact
                path={Urls.account}
              >
                <Authenticated>
                  <EmailVerified>
                    <Account />
                  </EmailVerified>
                </Authenticated>
              </Route>
            )}
            <Route exact path={Urls.accountContact}>
              <Authenticated>
                <EmailVerified>
                  <AccountContact />
                </EmailVerified>
              </Authenticated>
            </Route>
            {
              myAccountV2 && (
                <PrivateRoute
                  exact
                  path={Urls.aboutKueski}
                  component={AboutKueski}
                />
              )
            }
            <Route
              exact
              path={Urls.twoFactorVerificationActivity}
            >
              <Verification2FA />
            </Route>
            <Route exact path={Urls.startVerification}>
              <Authenticated skipRedirect>
                <EmailVerified>
                  <UserVerification />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route exact path={Urls.resultVerification}>
              <Authenticated skipRedirect>
                <EmailVerified>
                  <UserVerificationResult />
                </EmailVerified>
              </Authenticated>
            </Route>
            <Route
              exact
              path={Urls.externalError}
            >
              <ExternalError />
            </Route>
            <Route
              exact
              path={Urls.emailVerifyToken}
            >
              <EmailVerificationToken />
            </Route>
            <Route
              component={PagerRedesignLanding}
            />
          </Switch>
        </PrevPageProvider>
      </ConnectedRouter>
    </Provider>
  );
};

export default Routes;
